/**
 * 🚗 V2X车辆管理器 - 数据驱动车辆3D可视化系统
 *
 * 设计理念：
 * 1. 📊 数据驱动：专为RSM协议数据优化，自动处理车辆生命周期
 * 2. 🎯 高性能：支持大规模车辆渲染，批量更新优化
 * 3. 🔄 实时同步：位置、状态、属性实时更新和动画
 * 4. 🎨 智能交互：自动绑定点击事件和气泡系统
 * 5. 🌐 地理坐标：完整支持地理坐标与3D世界坐标转换
 */

import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import type { BubbleManager, BubbleConfig } from './BubbleManager';
import type { CoordinateSystem } from './CoordinateSystem';
import type { VehicleData } from './RSMDataAdapter';
import { RSMDataAdapter } from './RSMDataAdapter';

// 动态导入GSAP
let gsap: any = null;
(async () => {
  try {
    const gsapModule = await import('gsap');
    gsap = gsapModule.default || gsapModule;
    console.log('[VehicleManager] GSAP加载成功，将使用高级动画。');
  } catch (error) {
    console.warn('[VehicleManager] GSAP未找到，将使用基础动画。');
  }
})();

// === 类型定义 ===

/**
 * 车辆类型枚举（基于RSM ptcType标准化）
 */
export type VehicleType =
  | 'unknown' // 未知类型
  | 'vehicle' // 机动车
  | 'non_motor_vehicle' // 非机动车
  | 'pedestrian' // 行人
  | 'rsu'; // RSU设备

/**
 * 车辆状态枚举（基于实际运行状态）
 */
export type VehicleStatus =
  | 'moving' // 移动中
  | 'stopped' // 停止
  | 'parked' // 停放
  | 'offline' // 离线
  | 'warning' // 警告状态
  | 'error'; // 错误状态

/**
 * 车辆移动配置接口
 */
export interface MoveToConfig {
  /** 目标地理坐标 */
  latitude: number;
  longitude: number;
  altitude?: number;
  /** 移动持续时间（秒） */
  duration?: number;
  /** 目标航向角（弧度） */
  heading?: number;
  /** 动画缓动函数 */
  ease?: string;
  /** 完成回调 */
  onComplete?: () => void;
  /** 进度回调 */
  onUpdate?: (progress: number) => void;
}

/**
 * 车辆移动结果
 */
export interface MoveResult {
  success: boolean;
  distance: number;
  duration: number;
  error?: string;
}

/**
 * 手动车辆配置（用于addVehicle）
 */
export interface VehicleConfig {
  /** 车辆唯一标识 */
  id: string;
  /** 车辆名称 */
  name: string;
  /** 车辆类型 */
  type: VehicleType;
  /** 初始状态 */
  status?: VehicleStatus;
  /** 3D世界坐标位置 */
  position?: THREE.Vector3;
  /** 地理坐标位置 */
  geographicPosition?: {
    latitude: number;
    longitude: number;
    altitude?: number;
  };
  /** 初始航向角（弧度） */
  heading?: number;
  /** 车辆3D模型URL */
  modelUrl?: string;
  /** 车辆缩放比例 */
  scale?: number;
  /** 自定义数据 */
  data?: Record<string, any>;
  /** 点击回调 */
  onClick?: (controller: VehicleController) => void;
  /** 气泡配置 */
  bubbleConfig?: Partial<BubbleConfig>;
}

/**
 * 车辆实例信息（运行时状态）
 */
export interface VehicleInfo extends VehicleConfig {
  /** 3D对象引用 */
  object3D: THREE.Object3D;
  /** 当前状态 */
  status: VehicleStatus;
  /** RSM参与者数据 */
  rsmData?: VehicleData;
  /** 是否显示气泡 */
  bubbleVisible: boolean;
  /** 创建时间 */
  createdAt: Date;
  /** 最后更新时间 */
  updatedAt: Date;
  /** GSAP动画实例 */
  gsapTween?: any;
  /** 动画混合器 */
  mixer?: THREE.AnimationMixer;
  /** 车轮动画 */
  wheelAction?: THREE.AnimationAction;
}

/**
 * 兼容旧版本的移动配置
 */
export interface MoveAnimationConfig {
  targetGeographicPosition: {
    latitude: number;
    longitude: number;
    altitude?: number;
  };
  duration?: number;
  targetHeading?: number;
  ease?: string;
  onComplete?: () => void;
  onUpdate?: (progress: number) => void;
}

// === VehicleController 类 ===

/**
 * 🎮 车辆控制器 - 单个车辆的操作接口
 */
export class VehicleController {
  private vehicleInfo: VehicleInfo;
  private manager: VehicleManager;

  constructor(vehicleInfo: VehicleInfo, manager: VehicleManager) {
    this.vehicleInfo = vehicleInfo;
    this.manager = manager;
  }

  // === 基本属性访问 ===
  get id(): string {
    return this.vehicleInfo.id;
  }
  get name(): string {
    return this.vehicleInfo.name;
  }
  get type(): VehicleType {
    return this.vehicleInfo.type;
  }
  get status(): VehicleStatus {
    return this.vehicleInfo.status;
  }
  get object3D(): THREE.Object3D {
    return this.vehicleInfo.object3D;
  }
  get info(): VehicleInfo {
    return this.vehicleInfo;
  }
  get rsmData(): VehicleData | undefined {
    return this.vehicleInfo.rsmData;
  }

  /**
   * 获取当前地理坐标
   */
  get geographicPosition():
    | { latitude: number; longitude: number; altitude?: number }
    | undefined {
    if (!this.manager.coordinateSystem) return undefined;
    const worldPos = this.vehicleInfo.object3D.position;
    return this.manager.coordinateSystem.getGeographicFromVector3(worldPos);
  }

  /**
   * 移动到指定地理坐标
   */
  moveTo(config: MoveToConfig): MoveResult {
    const result = this.manager.moveVehicleToLocationWithResult(this.id, {
      targetGeographicPosition: {
        latitude: config.latitude,
        longitude: config.longitude,
        altitude: config.altitude,
      },
      duration: config.duration,
      targetHeading: config.heading,
      ease: config.ease,
      onComplete: config.onComplete,
      onUpdate: config.onUpdate,
    });

    return result;
  }

  /**
   * 设置航向角
   */
  setHeading(heading: number): void {
    this.vehicleInfo.object3D.rotation.y = heading;
    this.vehicleInfo.updatedAt = new Date();
  }

  /**
   * 设置车辆状态
   */
  setStatus(status: VehicleStatus): void {
    this.vehicleInfo.status = status;
    this.vehicleInfo.updatedAt = new Date();
  }

  /**
   * 更新自定义数据
   */
  updateData(data: Record<string, any>): void {
    this.vehicleInfo.data = { ...this.vehicleInfo.data, ...data };
    this.vehicleInfo.updatedAt = new Date();
  }

  /**
   * 显示气泡
   */
  showBubble(): boolean {
    return this.manager.showVehicleBubble(this.id);
  }

  /**
   * 隐藏气泡
   */
  hideBubble(): boolean {
    return this.manager.hideVehicleBubble(this.id);
  }

  /**
   * 切换气泡显示状态
   */
  toggleBubble(): boolean {
    return this.vehicleInfo.bubbleVisible
      ? this.hideBubble()
      : this.showBubble();
  }
}

// === VehicleManager 类 ===

/**
 * 🚗 车辆管理器 - 核心车辆管理系统
 */
export class VehicleManager {
  private scene: THREE.Scene;
  private camera: THREE.Camera;
  private container: HTMLElement;
  private bubbleManager?: BubbleManager;
  public coordinateSystem?: CoordinateSystem;

  // 车辆数据
  private vehicles: Map<string, VehicleInfo> = new Map();
  private vehicleGroup: THREE.Group;
  private modelLoader: GLTFLoader;
  private defaultVehicleModelUrl: string;

  // 交互系统
  private raycaster = new THREE.Raycaster();
  private mouse = new THREE.Vector2();
  private boundClick = this.handleClick.bind(this);

  // 车辆样式配置
  private readonly VEHICLE_STYLES: Record<
    VehicleType,
    { color: number; size: number }
  > = {
    unknown: { color: 0x888888, size: 4 },
    vehicle: { color: 0x4a90e2, size: 4 },
    non_motor_vehicle: { color: 0x7ed321, size: 2 },
    pedestrian: { color: 0xf5a623, size: 1 },
    rsu: { color: 0xd0021b, size: 6 },
  };

  constructor(
    scene: THREE.Scene,
    camera: THREE.Camera,
    container: HTMLElement,
    bubbleManager?: BubbleManager,
    coordinateSystem?: CoordinateSystem,
    defaultVehicleModelUrl?: string
  ) {
    this.scene = scene;
    this.camera = camera;
    this.container = container;
    this.bubbleManager = bubbleManager;
    this.coordinateSystem = coordinateSystem;
    // 🚗 设置默认车辆模型URL
    this.defaultVehicleModelUrl =
      defaultVehicleModelUrl ||
      'http://58.215.199.197:29002/roadmodal/zjp/su7.glb';

    // 初始化组件
    this.vehicleGroup = new THREE.Group();
    this.vehicleGroup.name = 'VehicleGroup';
    this.scene.add(this.vehicleGroup);

    this.modelLoader = new GLTFLoader();

    // 初始化交互系统
    this.initializeInteractionSystem();

    console.log(
      `[VehicleManager] 车辆管理器初始化完成，默认模型: ${this.defaultVehicleModelUrl}`
    );
  }

  /**
   * 初始化交互系统
   */
  private initializeInteractionSystem(): void {
    this.container.addEventListener('click', this.boundClick);
  }

  /**
   * 处理RSM参与者数据批量更新
   */
  async processRSMData(rsmParticipants: VehicleData[]): Promise<void> {
    const startTime = performance.now();

    for (const participant of rsmParticipants) {
      try {
        const existingVehicle = this.vehicles.get(
          participant.runtime.vehicleId
        );

        if (existingVehicle) {
          // 更新现有车辆
          this.updateVehicleFromRSMData(participant);
        } else {
          // 创建新车辆
          await this.createVehicleFromRSMData(participant);
        }
      } catch (error) {
        console.error(
          `[VehicleManager] 处理车辆 ${participant.runtime.vehicleId} 失败:`,
          error
        );
      }
    }

    const endTime = performance.now();
    console.log(
      `[VehicleManager] 处理 ${rsmParticipants.length} 个车辆数据耗时: ${endTime - startTime}ms`
    );
  }

  /**
   * 从RSM数据创建新车辆
   */
  private async createVehicleFromRSMData(
    participant: VehicleData
  ): Promise<void> {
    if (!this.coordinateSystem) {
      console.warn('[VehicleManager] 坐标系统未初始化，无法创建车辆');
      return;
    }

    // 转换地理坐标到3D世界坐标
    const worldPosition = this.coordinateSystem.createVector3FromGeographic({
      latitude: participant.latitude,
      longitude: participant.longitude,
      altitude: participant.elevation || 0,
    });

    // 创建车辆配置
    const vehicleConfig: VehicleConfig = {
      id: participant.runtime.vehicleId,
      name: participant.plateNo || `车辆-${participant.ptcId}`,
      type: this.mapPtcTypeToStandardType(participant.ptcType),
      status: this.inferStatusFromRSMData(participant),
      position: worldPosition,
      geographicPosition: {
        latitude: participant.latitude,
        longitude: participant.longitude,
        altitude: participant.elevation || 0,
      },
      heading: RSMDataAdapter.degreesToRadians(participant.heading),
      modelUrl: this.defaultVehicleModelUrl,
      scale: 1.0,
      data: { participant },
    };

    // 创建车辆
    await this.addVehicle(vehicleConfig);

    // 更新RSM数据
    const vehicleInfo = this.vehicles.get(participant.runtime.vehicleId);
    if (vehicleInfo) {
      vehicleInfo.rsmData = participant;
    }

    console.log(
      `[VehicleManager] 创建车辆: ${participant.runtime.vehicleId} (使用默认模型)`
    );
  }

  /**
   * 从RSM数据更新现有车辆 - 使用平滑动画
   */
  private updateVehicleFromRSMData(participant: VehicleData): void {
    const vehicleInfo = this.vehicles.get(participant.runtime.vehicleId);
    if (!vehicleInfo || !this.coordinateSystem) return;

    // 更新RSM数据
    vehicleInfo.rsmData = participant;
    vehicleInfo.updatedAt = new Date();

    // 更新状态
    vehicleInfo.status = this.inferStatusFromRSMData(participant);

    // 计算新的世界坐标位置
    const newWorldPosition = this.coordinateSystem.createVector3FromGeographic({
      latitude: participant.latitude,
      longitude: participant.longitude,
      altitude: participant.elevation || 0,
    });

    // 计算新的航向角
    const newHeading = RSMDataAdapter.degreesToRadians(participant.heading);

    // 🎬 修复：使用平滑动画更新位置和航向
    const currentPosition = vehicleInfo.object3D.position.clone();

    // 计算移动距离，决定动画时长
    const distance = currentPosition.distanceTo(newWorldPosition);
    const animationDuration = Math.min(Math.max(distance * 0.1, 0.3), 2.0); // 0.3-2秒之间

    // 停止之前的动画
    if (vehicleInfo.gsapTween) {
      vehicleInfo.gsapTween.kill();
    }

    // 使用GSAP创建平滑移动动画
    if (gsap && distance > 0.1) {
      // 只有移动距离足够大才播放动画
      vehicleInfo.gsapTween = gsap.to(vehicleInfo.object3D.position, {
        x: newWorldPosition.x,
        y: newWorldPosition.y,
        z: newWorldPosition.z,
        duration: animationDuration,
        ease: 'power2.out',
        onUpdate: () => {
          // 可以在这里添加车轮旋转等效果
          if (vehicleInfo.wheelAction) {
            vehicleInfo.wheelAction.play();
          }
        },
        onComplete: () => {
          vehicleInfo.gsapTween = null;
          if (vehicleInfo.wheelAction) {
            vehicleInfo.wheelAction.stop();
          }
        },
      });

      // 同时动画化航向角
      gsap.to(vehicleInfo.object3D.rotation, {
        y: newHeading,
        duration: animationDuration,
        ease: 'power2.out',
      });
    } else {
      // 距离太小或没有GSAP，直接设置位置
      vehicleInfo.object3D.position.copy(newWorldPosition);
      vehicleInfo.object3D.rotation.y = newHeading;
    }

    // 更新气泡内容
    if (vehicleInfo.bubbleVisible && this.bubbleManager) {
      this.updateVehicleBubbleContent(vehicleInfo);
    }

    console.log(
      `[VehicleManager] 平滑移动车辆 ${participant.runtime.vehicleId}: 距离=${distance.toFixed(2)}m, 动画时长=${animationDuration.toFixed(2)}s`
    );
  }

  /**
   * 从RSM数据推断车辆状态
   */
  private inferStatusFromRSMData(participant: VehicleData): VehicleStatus {
    if (participant.runtime.systemStatus) {
      return participant.runtime.systemStatus;
    }

    if (participant.speed > 1) return 'moving';
    if (participant.speed <= 1 && participant.speed > 0) return 'stopped';
    return 'parked';
  }

  /**
   * 将RSM ptcType映射为标准车辆类型
   */
  private mapPtcTypeToStandardType(ptcType: number): VehicleType {
    switch (ptcType) {
      case 1:
        return 'vehicle';
      case 2:
        return 'non_motor_vehicle';
      case 3:
        return 'pedestrian';
      case 4:
        return 'rsu';
      default:
        return 'unknown';
    }
  }

  /**
   * 手动添加车辆
   */
  async addVehicle(config: VehicleConfig): Promise<VehicleController> {
    // 检查车辆是否已存在
    if (this.vehicles.has(config.id)) {
      throw new Error(`车辆 ${config.id} 已存在`);
    }

    // 加载3D模型
    const { modelContainer, mixer, wheelAction } =
      await this.loadVehicleModel(config);

    // 设置位置
    if (config.position) {
      modelContainer.position.copy(config.position);
    } else if (config.geographicPosition && this.coordinateSystem) {
      const worldPos = this.coordinateSystem.createVector3FromGeographic(
        config.geographicPosition
      );
      modelContainer.position.copy(worldPos);
    }

    // 设置航向角
    if (config.heading !== undefined) {
      modelContainer.rotation.y = config.heading;
    }

    // 设置缩放
    if (config.scale !== undefined) {
      modelContainer.scale.setScalar(config.scale);
    }

    // 设置用户数据（用于点击检测）
    modelContainer.userData = { vehicleId: config.id };

    // 添加到场景
    this.vehicleGroup.add(modelContainer);

    // 创建车辆信息
    const vehicleInfo: VehicleInfo = {
      ...config,
      object3D: modelContainer,
      status: config.status || 'parked',
      bubbleVisible: false,
      createdAt: new Date(),
      updatedAt: new Date(),
      mixer,
      wheelAction,
    };

    // 存储车辆信息
    this.vehicles.set(config.id, vehicleInfo);

    // 创建气泡
    if (this.bubbleManager) {
      this.createVehicleBubble(vehicleInfo);
    }

    console.log(`[VehicleManager] 添加车辆: ${config.id}`);

    return new VehicleController(vehicleInfo, this);
  }

  /**
   * 移除车辆
   */
  async removeVehicle(vehicleId: string): Promise<boolean> {
    const vehicleInfo = this.vehicles.get(vehicleId);
    if (!vehicleInfo) {
      console.warn(`[VehicleManager] 车辆 ${vehicleId} 不存在`);
      return false;
    }

    try {
      // 停止动画
      if (vehicleInfo.gsapTween) {
        vehicleInfo.gsapTween.kill();
      }

      // 清理混合器
      if (vehicleInfo.mixer) {
        vehicleInfo.mixer.stopAllAction();
      }

      // 隐藏气泡
      if (vehicleInfo.bubbleVisible && this.bubbleManager) {
        this.bubbleManager.hideBubble(vehicleId);
      }

      // 从场景中移除
      this.vehicleGroup.remove(vehicleInfo.object3D);

      // 释放资源
      vehicleInfo.object3D.traverse(child => {
        if (child instanceof THREE.Mesh) {
          child.geometry?.dispose();
          if (Array.isArray(child.material)) {
            child.material.forEach(material => material?.dispose());
          } else {
            child.material?.dispose();
          }
        }
      });

      // 从存储中移除
      this.vehicles.delete(vehicleId);

      console.log(`[VehicleManager] 移除车辆: ${vehicleId}`);
      return true;
    } catch (error) {
      console.error(`[VehicleManager] 移除车辆 ${vehicleId} 失败:`, error);
      return false;
    }
  }

  /**
   * 获取所有车辆控制器
   */
  getAllVehicles(): VehicleController[] {
    return Array.from(this.vehicles.values()).map(
      vehicleInfo => new VehicleController(vehicleInfo, this)
    );
  }

  /**
   * 获取指定车辆控制器
   */
  getVehicle(vehicleId: string): VehicleController | undefined {
    const vehicleInfo = this.vehicles.get(vehicleId);
    return vehicleInfo ? new VehicleController(vehicleInfo, this) : undefined;
  }

  /**
   * 按类型获取车辆
   */
  getVehiclesByType(type: VehicleType): VehicleController[] {
    return Array.from(this.vehicles.values())
      .filter(vehicleInfo => vehicleInfo.type === type)
      .map(vehicleInfo => new VehicleController(vehicleInfo, this));
  }

  /**
   * 按状态获取车辆
   */
  getVehiclesByStatus(status: VehicleStatus): VehicleController[] {
    return Array.from(this.vehicles.values())
      .filter(vehicleInfo => vehicleInfo.status === status)
      .map(vehicleInfo => new VehicleController(vehicleInfo, this));
  }

  // === 移动和动画方法 ===

  /**
   * 移动车辆到指定位置（带详细结果）
   */
  moveVehicleToLocationWithResult(
    vehicleId: string,
    config: MoveAnimationConfig
  ): MoveResult {
    const vehicle = this.vehicles.get(vehicleId);
    if (!vehicle || !this.coordinateSystem) {
      return {
        success: false,
        distance: 0,
        duration: 0,
        error: '车辆未找到或坐标系统未初始化',
      };
    }

    const startPosition = vehicle.object3D.position.clone();
    const targetPosition = this.coordinateSystem.createVector3FromGeographic(
      config.targetGeographicPosition
    );

    const distance = startPosition.distanceTo(targetPosition);
    const duration = config.duration ?? 2;

    // 计算转向角
    let targetHeading = config.targetHeading;
    if (targetHeading === undefined) {
      targetHeading = Math.atan2(
        targetPosition.x - startPosition.x,
        targetPosition.z - startPosition.z
      );
    }

    // 如果距离太小，不执行移动
    if (distance < 0.01) {
      return {
        success: false,
        distance: 0,
        duration: 0,
        error: '目标位置与当前位置相同',
      };
    }

    // 执行移动动画
    vehicle.status = 'moving';
    this.updateVehicleBubbleContent(vehicle);

    const onComplete = () => {
      vehicle.status = 'stopped';
      this.updateVehicleBubbleContent(vehicle);
      if (config.onComplete) {
        config.onComplete();
      }
    };

    if (gsap) {
      vehicle.gsapTween = gsap.to(vehicle.object3D.position, {
        x: targetPosition.x,
        y: targetPosition.y,
        z: targetPosition.z,
        duration,
        ease: config.ease || 'power2.inOut',
        onUpdate: () => {
          vehicle.object3D.updateMatrix();
          vehicle.object3D.updateMatrixWorld(true);
          if (config.onUpdate) {
            config.onUpdate(vehicle.gsapTween.progress());
          }
        },
        onComplete,
      });

      // 旋转动画
      gsap.to(vehicle.object3D.rotation, {
        y: targetHeading,
        duration: duration * 0.8,
        ease: config.ease || 'power2.inOut',
      });
    } else {
      // 无GSAP时的基础移动
      vehicle.object3D.position.copy(targetPosition);
      vehicle.object3D.rotation.y = targetHeading;
      onComplete();
    }

    return {
      success: true,
      distance,
      duration,
    };
  }

  /**
   * 移动车辆（兼容旧版API）
   */
  moveVehicleToLocation(
    vehicleId: string,
    config: MoveAnimationConfig
  ): boolean {
    const result = this.moveVehicleToLocationWithResult(vehicleId, config);
    return result.success;
  }

  // === 气泡管理方法 ===

  /**
   * 显示车辆气泡
   */
  showVehicleBubble(vehicleId: string): boolean {
    const vehicle = this.vehicles.get(vehicleId);
    if (!vehicle || !this.bubbleManager) return false;

    const success = this.bubbleManager.showBubble(
      `vehicle-bubble-${vehicle.id}`
    );
    if (success) vehicle.bubbleVisible = true;
    return success;
  }

  /**
   * 隐藏车辆气泡
   */
  hideVehicleBubble(vehicleId: string): boolean {
    const vehicle = this.vehicles.get(vehicleId);
    if (!vehicle || !this.bubbleManager) return false;

    const success = this.bubbleManager.hideBubble(
      `vehicle-bubble-${vehicle.id}`
    );
    if (success) vehicle.bubbleVisible = false;
    return success;
  }

  /**
   * 更新车辆气泡内容
   */
  updateVehicleBubbleContent(vehicle: VehicleInfo): void {
    if (!this.bubbleManager) return;

    this.bubbleManager.updateBubbleContent(`vehicle-bubble-${vehicle.id}`, {
      data: {
        ID: vehicle.id,
        类型: this.getVehicleTypeDisplayName(vehicle.type),
        状态: this.getVehicleStatusDisplayName(vehicle.status),
        车牌: vehicle.data?.plateNo || '未知',
        速度: vehicle.data?.speed
          ? `${vehicle.data.speed.toFixed(1)} m/s`
          : '0 m/s',
        更新时间: new Date(vehicle.updatedAt).toLocaleTimeString(),
        ...vehicle.data,
      },
    });
  }

  /**
   * 创建车辆气泡
   */
  private createVehicleBubble(vehicle: VehicleInfo): void {
    if (!this.bubbleManager) return;

    const bubbleConfig: BubbleConfig = {
      id: `vehicle-bubble-${vehicle.id}`,
      title: vehicle.name,
      target: vehicle.object3D,
      content: {
        data: {
          ID: vehicle.id,
          类型: this.getVehicleTypeDisplayName(vehicle.type),
          状态: this.getVehicleStatusDisplayName(vehicle.status),
          ...vehicle.data,
        },
      },
      type: 'info',
      maxWidth: 300,
      ...vehicle.bubbleConfig,
    };

    this.bubbleManager.addBubble(bubbleConfig);
  }

  // === 3D模型加载方法 ===

  /**
   * 加载车辆3D模型
   */
  private async loadVehicleModel(config: VehicleConfig): Promise<{
    modelContainer: THREE.Group;
    mixer?: THREE.AnimationMixer;
    wheelAction?: THREE.AnimationAction;
  }> {
    const modelContainer = new THREE.Group();
    modelContainer.name = `vehicle-${config.id}`;

    try {
      if (config.modelUrl) {
        // 加载自定义模型
        const gltf = await this.modelLoader.loadAsync(config.modelUrl);
        const model = gltf.scene;
        modelContainer.add(model);

        // 设置阴影
        model.traverse(child => {
          if (child instanceof THREE.Mesh) {
            child.castShadow = true;
            child.receiveShadow = true;
          }
        });

        // 处理动画
        let mixer: THREE.AnimationMixer | undefined;
        let wheelAction: THREE.AnimationAction | undefined;
        if (gltf.animations && gltf.animations.length > 0) {
          mixer = new THREE.AnimationMixer(model);
          wheelAction = mixer.clipAction(gltf.animations[0]);
        }

        return { modelContainer, mixer, wheelAction };
      } else {
        // 使用默认几何体
        const style = this.VEHICLE_STYLES[config.type];
        const geometry = new THREE.BoxGeometry(
          style.size,
          style.size * 0.5,
          style.size * 2
        );
        const material = new THREE.MeshStandardMaterial({
          color: style.color,
          roughness: 0.7,
          metalness: 0.3,
        });
        const mesh = new THREE.Mesh(geometry, material);
        mesh.castShadow = true;
        mesh.receiveShadow = true;
        modelContainer.add(mesh);

        return { modelContainer };
      }
    } catch (error) {
      console.error(`[VehicleManager] 模型加载失败 ${config.id}:`, error);

      // 加载失败时使用备用几何体
      const style =
        this.VEHICLE_STYLES[config.type] || this.VEHICLE_STYLES.unknown;
      const geometry = new THREE.BoxGeometry(
        style.size,
        style.size * 0.5,
        style.size * 2
      );
      const material = new THREE.MeshStandardMaterial({
        color: 0xff0000, // 红色表示加载失败
        roughness: 0.7,
        metalness: 0.3,
      });
      const mesh = new THREE.Mesh(geometry, material);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      modelContainer.add(mesh);

      return { modelContainer };
    }
  }

  // === 交互事件处理 ===

  /**
   * 处理点击事件
   */
  private handleClick(event: MouseEvent): void {
    const rect = this.container.getBoundingClientRect();
    this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    this.raycaster.setFromCamera(this.mouse, this.camera);
    const intersects = this.raycaster.intersectObjects(
      this.vehicleGroup.children,
      true
    );

    if (intersects.length > 0) {
      const vehicleId = this.findVehicleId(intersects[0].object);
      if (vehicleId) {
        this.handleVehicleClick(vehicleId);
      }
    }
  }

  /**
   * 查找车辆ID
   */
  private findVehicleId(object: THREE.Object3D): string | null {
    let current = object;
    while (current) {
      if (current.name && current.name.startsWith('vehicle-')) {
        return current.name.replace('vehicle-', '');
      }
      current = current.parent!;
    }
    return null;
  }

  /**
   * 处理车辆点击
   */
  private handleVehicleClick(vehicleId: string): void {
    const vehicle = this.vehicles.get(vehicleId);
    if (!vehicle) return;

    // 切换气泡显示
    const controller = new VehicleController(vehicle, this);
    controller.toggleBubble();

    // 调用自定义点击回调
    if (vehicle.onClick) {
      vehicle.onClick(controller);
    }
  }

  // === 更新和清理方法 ===

  /**
   * 更新动画（每帧调用）
   */
  update(delta: number): void {
    for (const vehicle of this.vehicles.values()) {
      if (vehicle.mixer) {
        vehicle.mixer.update(delta);
      }
    }
  }

  /**
   * 清理资源
   */
  dispose(): void {
    // 移除事件监听器
    this.container.removeEventListener('click', this.boundClick);

    // 清理所有车辆
    const vehicleIds = Array.from(this.vehicles.keys());
    vehicleIds.forEach(id => this.removeVehicle(id));

    // 移除车辆组
    this.scene.remove(this.vehicleGroup);

    console.log('[VehicleManager] 资源清理完成');
  }

  // === 工具方法 ===

  /**
   * 获取车辆类型显示名称
   */
  private getVehicleTypeDisplayName(type: VehicleType): string {
    const typeNames: Record<VehicleType, string> = {
      unknown: '未知',
      vehicle: '机动车',
      non_motor_vehicle: '非机动车',
      pedestrian: '行人',
      rsu: 'RSU设备',
    };
    return typeNames[type];
  }

  /**
   * 获取车辆状态显示名称
   */
  private getVehicleStatusDisplayName(status: VehicleStatus): string {
    const statusNames: Record<VehicleStatus, string> = {
      moving: '移动中',
      stopped: '停止',
      parked: '停放',
      offline: '离线',
      warning: '警告',
      error: '错误',
    };
    return statusNames[status];
  }
}

// === 工厂函数 ===

/**
 * 创建车辆管理器实例
 */
export function createVehicleManager(
  scene: THREE.Scene,
  camera: THREE.Camera,
  container: HTMLElement,
  bubbleManager?: BubbleManager,
  coordinateSystem?: CoordinateSystem,
  defaultVehicleModelUrl?: string
): VehicleManager {
  return new VehicleManager(
    scene,
    camera,
    container,
    bubbleManager,
    coordinateSystem,
    defaultVehicleModelUrl
  );
}
